import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AxiosInstance from "@/utils/AxiosInstance";

const IMPORT_POLL_INTERVAL_MS = 12_000;

type UploadConfig = {
  label: string;
  endpoint: string;
  inputId: string;
  filename: string;
  requiredNote: string;
  templateHeaders: string[];
};


type LegacyBulkUploadsProps = {
  organizations?: Array<{
    _id: string;
    organizationName?: string;
    name?: string;
  }>;
};

type UploadErrorRow = {
  row: number;
  sourcePatientId?: string;
  error: string;
};

type ImportProgress = {
  importBatchId: string;
  label: string;
  endpoint: string;
  status: string;
  total: number;
  created: number;
  failed: number;
  remaining: number;
  percentComplete: number;
};

const uploadConfigs: UploadConfig[] = [
  {
    label: "Care Plans",
    endpoint: "/legacy-care-plans/bulk",
    inputId: "legacy-care-plans-upload",
    filename: "past-medical-records-care-plans-template.xlsx",
    requiredNote: "ID, patient name, DOB, ICD codes, insurance, address, provider, program, questions and answers are required.",
    templateHeaders: [
      "ID",
      "Patient name",
      "DOB",
      "ICD Codes",
      "Diagnostic (Careplan ICD)",
      "Primary Insurance",
      "Insurance ID",
      "Address",
      "Added By",
      "Provider",
      "Program",
      "Date of creation",
      "Question",
      "Answer",
      "Question",
      "Answer",
      "Question",
      "Answer",
      "ICD 1 Question",
      "ICD 1 Answer",
      "ICD 2 Question",
      "ICD 2 Answer",
    ],
  },
  {
    label: "Treatment Plans",
    endpoint: "/legacy-treatment-plans/bulk",
    inputId: "legacy-treatment-plans-upload",
    filename: "past-medical-records-treatment-plans-template.xlsx",
    requiredNote: "ID, patient name, DOB, ICD codes, insurance, address, provider, program, questions and answers are required.",
    templateHeaders: [
      "ID",
      "Patient name",
      "DOB",
      "ICD Codes",
      "Primary Insurance",
      "Insurance ID",
      "Address",
      "Added By",
      "Provider",
      "Program",
      "Date of creation",
      "Question",
      "Answer",
      "Question",
      "Answer",
      "Question",
      "Answer",
      "Question",
      "Answer",
      "Question",
      "Answer",
    ],
  },
  {
    label: "Nursing Notes",
    endpoint: "/legacy-nursing-notes/bulk",
    inputId: "legacy-nursing-notes-upload",
    filename: "past-medical-records-nursing-notes-template.xlsx",
    requiredNote: "ID, DOB, ICD codes, insurance, address, program, care type, date/time, duration, and note are required.",
    templateHeaders: [
      "ID",
      "Patient name",
      "DOB",
      "ICD Codes",
      "Primary Insurance",
      "Insurance ID",
      "Address",
      "Program",
      "Care Type",
      "Date & Time",
      "Duration",
      "Note",
    ],
  },
  {
    label: "Readings",
    endpoint: "/legacy-readings/bulk",
    inputId: "legacy-readings-upload",
    filename: "past-medical-records-readings-template.xlsx",
    requiredNote: "ID, patient name, DOB, device assigned, date, BP, pulse, SpO2, weight, and glucose level are required when available.",
    templateHeaders: [
      "ID",
      "Patient name",
      "DOB",
      "ICD Codes",
      "Primary Insurance",
      "Insurance ID",
      "Address",
      "Device Assigned",
      "Date",
      "BP (Systolic/Diastolic)",
      "Pulse",
      "SpO2",
      "Weight (lbs)",
      "Glucose Level",
    ],
  },
  {
    label: "Reading Count",
    endpoint: "/legacy-reading-counts/bulk",
    inputId: "legacy-reading-count-upload",
    filename: "past-medical-records-reading-count-template.xlsx",
    requiredNote: "ID, patient name, DOB, total reading count, and count month/year are required.",
    templateHeaders: [
      "ID",
      "Patient Name",
      "DOB",
      "Total Reading Count",
      "Count (Month/Year)",
    ],
  },
];

function normalizeImportProgress(
  payload: any,
  options: { fallbackBatchId?: string; label: string; endpoint: string },
): ImportProgress | null {
  const raw = payload?.data?.data || payload?.data || payload || {};
  const importBatchId = raw.importBatchId || options.fallbackBatchId;
  if (!importBatchId) return null;

  return {
    importBatchId,
    label: options.label,
    endpoint: options.endpoint,
    status: String(raw.status || "processing"),
    total: Number(raw.total) || 0,
    created: Number(raw.created) || 0,
    failed: Number(raw.failed) || 0,
    remaining: Number(raw.remaining) || 0,
    percentComplete: Number(raw.percentComplete) || 0,
  };
}

function isImportComplete(progress: ImportProgress) {
  const status = progress.status.toLowerCase();
  if (["completed", "complete", "done", "success", "failed", "error"].includes(status)) {
    return true;
  }
  if (progress.percentComplete >= 100) return true;
  if (progress.total > 0 && progress.remaining === 0) return true;
  return false;
}

export function LegacyNursingNotesUpload({ organizations = [] }: LegacyBulkUploadsProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isUploadErrorsOpen, setIsUploadErrorsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<UploadErrorRow[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState("all");
  const [selectedUpload, setSelectedUpload] = useState(uploadConfigs[0].endpoint);
  const [isImportProgressOpen, setIsImportProgressOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [isPollingProgress, setIsPollingProgress] = useState(false);
  const config = uploadConfigs.find((upload) => upload.endpoint === selectedUpload) || uploadConfigs[0];

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => clearPollTimer(), []);

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      config.templateHeaders.reduce<Record<string, string>>((acc, header) => {
        acc[header] = "";
        return acc;
      }, {}),
    ]);
    worksheet["!cols"] = config.templateHeaders.map((header) => ({
      wch: Math.max(header.length + 4, 16),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, config.label);
    XLSX.writeFile(workbook, config.filename);
    toast.success(`${config.label} template downloaded successfully.`);
  };

  const finishImport = (progress: ImportProgress, errors: any[] = []) => {
    clearPollTimer();
    setIsPollingProgress(false);
    setImportProgress(progress);

    const summaryMessage = `${progress.label} import completed: ${progress.created} created, ${progress.failed} failed`;
    if (progress.failed > 0 || errors.length > 0) {
      toast.info(summaryMessage);
      if (errors.length > 0) {
        setUploadErrors(
          errors.map((item: any) => ({
            row: item.row ?? 0,
            sourcePatientId: item.sourcePatientId,
            error: item.error || "Unknown error",
          })),
        );
        setIsUploadErrorsOpen(true);
      }
    } else {
      toast.success(summaryMessage);
    }
  };

  const fetchImportProgress = async (progressMeta: Pick<ImportProgress, "importBatchId" | "label" | "endpoint">) => {
    setIsPollingProgress(true);
    try {
      const response: any = await AxiosInstance().get(
        `${progressMeta.endpoint}/${progressMeta.importBatchId}`,
      );
      const progress = normalizeImportProgress(response, {
        fallbackBatchId: progressMeta.importBatchId,
        label: progressMeta.label,
        endpoint: progressMeta.endpoint,
      });
      if (!progress) return;

      setImportProgress(progress);

      if (isImportComplete(progress)) {
        const errors =
          response?.data?.data?.errors ||
          response?.data?.errors ||
          [];
        finishImport(progress, errors);
      }
    } catch (error: any) {
      clearPollTimer();
      setIsPollingProgress(false);
      toast.error(
        error?.response?.data?.message ||
          `Failed to fetch ${progressMeta.label.toLowerCase()} import progress.`,
      );
    }
  };

  const startImportProgressPolling = (initial: ImportProgress) => {
    clearPollTimer();
    setImportProgress(initial);
    setIsImportProgressOpen(true);
    setOpen(false);

    if (isImportComplete(initial)) {
      finishImport(initial);
      return;
    }

    const progressMeta = {
      importBatchId: initial.importBatchId,
      label: initial.label,
      endpoint: initial.endpoint,
    };

    void fetchImportProgress(progressMeta);
    pollTimerRef.current = setInterval(() => {
      void fetchImportProgress(progressMeta);
    }, IMPORT_POLL_INTERVAL_MS);
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!config.endpoint) {
      toast.info("Upload API for this past medical records sheet is not configured yet.");
      event.target.value = "";
      return;
    }

    if (!selectedOrgId || selectedOrgId === "all" || selectedOrgId === "anthro-care") {
      toast.error("Please select a specific organization before uploading.");
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("excelFile", file);
    formData.append("organizationId", selectedOrgId);

    setUploading(true);
    try {
      const response: any = await AxiosInstance().post(config.endpoint, formData, {
        isFormData: true,
        timeout: 5 * 60 * 1000,
      } as any);
      const { data } = response;
      const result = data?.data || data || {};

      if (result.importBatchId) {
        const progress = normalizeImportProgress(result, {
          fallbackBatchId: result.importBatchId,
          label: config.label,
          endpoint: config.endpoint,
        });
        if (progress) {
          toast.success(
            data?.message || `${config.label} import started in the background.`,
          );
          startImportProgressPolling(progress);
          event.target.value = "";
          return;
        }
      }

      const { created = 0, failed = 0, errors = [] } = result;
      const summaryMessage = `Bulk upload completed: ${created} created, ${failed} failed`;

      if (failed > 0) {
        toast.info(summaryMessage);
        setUploadErrors(
          errors.map((item: any) => ({
            row: item.row ?? 0,
            sourcePatientId: item.sourcePatientId,
            error: item.error || "Unknown error",
          })),
        );
        setIsUploadErrorsOpen(true);
      } else {
        toast.success(summaryMessage);
      }

      event.target.value = "";
      setOpen(false);
    } catch (error: any) {
      const responseErrors = error?.response?.data?.errors || error?.response?.data?.data?.errors || [];
      if (responseErrors.length > 0) {
        setUploadErrors(
          responseErrors.map((item: any) => ({
            row: item.row ?? 0,
            sourcePatientId: item.sourcePatientId,
            error: item.error || "Unknown error",
          })),
        );
        setIsUploadErrorsOpen(true);
      }
      toast.error(
        error?.response?.data?.message || `Failed to upload past medical records of ${config.label.toLowerCase()}.`,
      );
    } finally {
      setUploading(false);
    }
  };

  const percent = Math.min(100, Math.max(0, importProgress?.percentComplete ?? 0));
  const isImportDone = importProgress ? isImportComplete(importProgress) : false;
  const progressLabel = importProgress?.label || config.label;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={uploading}
        onClick={() => setOpen(true)}
        className="px-4 py-2.5 rounded-lg font-medium text-sm transition-all shadow-sm hover:shadow flex items-center space-x-2 hover:bg-white hover:text-[#111827] hover:border-input"
      >
        <Upload className="mr-2 h-4 w-4" />
        {uploading ? "Uploading..." : "Upload"}
      </Button>
      <Dialog open={open} onOpenChange={(next) => !uploading && setOpen(next)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Upload</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select
              value={selectedUpload}
              onValueChange={setSelectedUpload}
              disabled={uploading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select sheet type" />
              </SelectTrigger>
              <SelectContent>
                {uploadConfigs.map((upload) => (
                  <SelectItem key={upload.endpoint} value={upload.endpoint}>
                    {upload.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedOrgId}
              onValueChange={setSelectedOrgId}
              disabled={uploading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="max-h-[260px] overflow-y-auto">
                <SelectItem value="all">All organizations</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org._id} value={org._id}>
                    {org.organizationName || org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={downloadTemplate}
              disabled={uploading}
            >
              <Download className="h-4 w-4" />
              Template
            </Button>
            <Input
              ref={inputRef}
              id={config.inputId}
              type="file"
              accept=".xlsx,.xls"
              disabled={uploading}
              onChange={handleUpload}
            />
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              <span className="font-semibold">Note:</span> {config.requiredNote}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isImportProgressOpen}
        onOpenChange={(next) => {
          if (!next && !isImportDone) return;
          setIsImportProgressOpen(next);
          if (!next) clearPollTimer();
        }}
      >
        <DialogContent className="max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{progressLabel} Import Progress</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground my-2">
              {isImportDone
                ? "Import finished."
                : "Do not close this window until the import is finished."}
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="capitalize text-muted-foreground">
                  Status: {importProgress?.status || "processing"}
                </span>
                <span className="font-medium">{percent}%</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="mt-1 font-semibold">
                  {(importProgress?.total ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Remaining</p>
                <p className="mt-1 font-semibold">
                  {(importProgress?.remaining ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Updated</p>
                <p className="mt-1 font-semibold text-emerald-700">
                  {(importProgress?.created ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="mt-1 font-semibold text-destructive">
                  {(importProgress?.failed ?? 0).toLocaleString()}
                </p>
              </div>
            </div>

            {isPollingProgress && !isImportDone && (
              <p className="text-xs text-muted-foreground">Checking progress…</p>
            )}

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setIsImportProgressOpen(false);
                  if (isImportDone) clearPollTimer();
                }}
                disabled={!isImportDone}
              >
                {isImportDone ? "Close" : "Processing…"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isUploadErrorsOpen} onOpenChange={setIsUploadErrorsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Errors</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {uploadErrors.length} row(s) could not be uploaded. Please fix the errors and upload again.
            </p>
            <div className="max-h-[60vh] overflow-auto rounded-md border border-destructive/30 bg-destructive/5">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-muted/80">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Row</th>
                    <th className="px-4 py-3 text-left font-medium">Patient ID</th>
                    <th className="px-4 py-3 text-left font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadErrors.map((item, index) => (
                    <tr key={`${item.row}-${index}`} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-2.5 font-medium">{item.row}</td>
                      <td className="px-4 py-2.5">{item.sourcePatientId || "-"}</td>
                      <td className="px-4 py-2.5 text-destructive">{item.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setIsUploadErrorsOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
